import React, { useState, useEffect, useRef } from "react";
import { Button } from "../Marketing/Button/Button";
import "./FeedbackBanner.scss";

const FeedbackBanner = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [isSticky, setIsSticky] = useState(false);
  const [isMigration, setIsMigration] = useState(false);
  const bannerRef = useRef(null);
  const placeholderRef = useRef(null);

  // Configuration - QUICK RELEASE: Hardcoded for now
  // TODO: Future improvements:
  // - Move config to environment variables or backend API
  // - Support multiple banners/campaigns
  // - Add analytics tracking for banner impressions and clicks
  // - Consider A/B testing different messages
  // - Add role-based targeting (show different messages to different user types)
  const config = {
    surveyUrl: "https://forms.cloud.microsoft/r/Pn1wfLNP9g", // TODO: Replace with actual survey URL
    expirationDate: new Date("2025-09-30"), // QUICK RELEASE: Hardcoded date
    sessionStorageKey: "feedbackBannerDismissed", // QUICK RELEASE: Session-only persistence
    // TODO: Future - use localStorage with timestamp for more sophisticated dismissal logic
    // TODO: Future - track dismissal in backend for analytics
  };

  useEffect(() => {
    console.log("DEBUG: FeedbackBanner useEffect");
    const urlParams = new URLSearchParams(window.location.search); 
    console.log(urlParams);
    const isMigration = urlParams.get('migration') === 'complete';
    const showWelcome = localStorage.getItem('migration_completed') === 'true';
    console.log(isMigration, showWelcome);
    if (isMigration || showWelcome) {
      setIsVisible(true);
      setIsMigration(true);
    }
  }, []);

  useEffect(() => {
    const now = new Date();
    const isDismissed = sessionStorage.getItem(config.sessionStorageKey);
    
    // Show banner if not expired and not dismissed in current session
    if (now <= config.expirationDate && !isDismissed) {
      setIsVisible(true);
    }
  }, [config.expirationDate, config.sessionStorageKey]);

  useEffect(() => {
    if (!isVisible) return;

    const updateBannerPosition = () => {
      if (placeholderRef.current && bannerRef.current) {
        const rect = placeholderRef.current.getBoundingClientRect();
        const threshold = 1;
        const shouldBeSticky = rect.top <= threshold;
        setIsSticky(shouldBeSticky);
        
        // QUICK RELEASE: Direct DOM manipulation for sidebar adjustment
        // TODO: Future - consider using React context or state management for cleaner integration
        // Adjust for sidebar when sticky
        if (shouldBeSticky) {
          const sidebar = document.querySelector('.left-sidebar');
          if (sidebar) {
            const sidebarWidth = sidebar.offsetWidth;
            const isExpanded = sidebar.classList.contains('expanded');
            bannerRef.current.style.left = isExpanded ? `${sidebarWidth}px` : '0';
            bannerRef.current.style.width = isExpanded ? `calc(100% - ${sidebarWidth}px)` : '100%';
          }
        } else {
          // Reset when not sticky
          bannerRef.current.style.left = '';
          bannerRef.current.style.width = '';
        }
      }
    };

    let ticking = false;
    const handleScroll = () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          updateBannerPosition();
          ticking = false;
        });
        ticking = true;
      }
    };

    // Listen for sidebar toggle
    const observers = [];
    const observeSidebar = () => {
      const sidebar = document.querySelector('.left-sidebar');
      if (sidebar) {
        // MutationObserver for class changes
        const mutationObserver = new MutationObserver(() => {
          // Update immediately when sidebar changes
          updateBannerPosition();
        });
        mutationObserver.observe(sidebar, { 
          attributes: true, 
          attributeFilter: ['class'],
          subtree: false 
        });
        observers.push(mutationObserver);
        
        // ResizeObserver for size changes
        const resizeObserver = new ResizeObserver(() => {
          updateBannerPosition();
        });
        resizeObserver.observe(sidebar);
        observers.push(resizeObserver);
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    updateBannerPosition(); // Check initial position
    
    observeSidebar();

    return () => {
      window.removeEventListener('scroll', handleScroll);
      observers.forEach(observer => {
        observer.disconnect();
      });
    };
  }, [isVisible]);

  const handleDismiss = () => {
    sessionStorage.setItem(config.sessionStorageKey, "true");
    setIsVisible(false);
  };

  const handleShareFeedback = () => {
    window.open(config.surveyUrl, "_blank", "noopener,noreferrer");
  };

  if (!isVisible) {
    return null;
  }

  

  return (
    <>
      {/* Placeholder to maintain layout when banner becomes sticky */}
      <div ref={placeholderRef} className="feedback-banner-placeholder">
        {isSticky && <div style={{ height: bannerRef.current?.offsetHeight || 0 }} />}
      </div>
      
      <div 
        ref={bannerRef}
        className={`feedback-banner ${isSticky ? 'feedback-banner-sticky' : ''}`}
      >
        <div className="feedback-banner-content">
          <div className="feedback-banner-icon">
            <i className="fas fa-star"></i>
          </div>
          
          <div className="feedback-banner-text">
            <div className="feedback-banner-title">
              {/* QUICK RELEASE: Hardcoded text - meets AC-2 requirement */}
              {/* TODO: Future - make this configurable, potentially with version number */}
              {isMigration ? "Welcome to the new MyLightedge Portal!" : "NEW RELEASE •"}
            </div>
            
            {!isMigration ? (
              <div className="feedback-banner-message">
                {/* QUICK RELEASE: Static message with docs link */}
                {/* TODO: Future - make message configurable from backend */}
                {/* TODO: Future - support markdown or rich text formatting */}
                Click <a href="https://docs.mylightedge.com/" target="_blank" rel="noopener noreferrer" className="feedback-banner-link">here</a> to learn about the new MyLightedge platform, and help us improve by sharing your feedback!
            </div>
            ) : (
              <div className="feedback-banner-message">
                {/* MESSAGE FOR MIGRATION COMPLETED */}

                You have successfully migrated your legacy Lightedge portal account to this new MyLightedge Portal. Thank you for enhancing your security by updating your password and MFA.
                <br />
                Click here <a href="https://docs.mylightedge.com/" target="_blank" rel="noopener noreferrer" className="feedback-banner-link">here</a> to learn about the new MyLightedge platform, and help us improve by sharing your feedback!
            </div>
            )}
          </div>

          <div className="feedback-banner-actions">
            <Button 
              variant="on-dark"
              size="sm"
              onClick={handleShareFeedback}
              icon="fas fa-comment-dots"
            >
              Share Feedback
            </Button>
            
            <Button
              variant="ghost"
              size="sm"
              className="feedback-banner-close"
              onClick={handleDismiss}
              aria-label="Dismiss banner"
              title="Dismiss banner"
            >
              <i className="fas fa-times"></i>
            </Button>
          </div>
        </div>
      </div>
    </>
  );
};

export default FeedbackBanner;